import { useEffect, useState } from 'react';
import { CheckoutOffer, resolveCheckoutOffer } from './offer';
import { fetchPriceBreakdown, PriceBreakdown } from './pricing';

/**
 * The credit line's label once a rescue is named on the offer.
 *
 * Short on purpose: the rescue is already named in the attribution row above the summary, so repeating
 * it here only makes the line wrap on a phone.
 */
const ADOPTION_CREDIT_LABEL = 'Adoption credit';

export interface CheckoutOfferState {
  /** True once resolution has finished, whether or not it found an offer. */
  resolved: boolean;
  offer: CheckoutOffer | null;
  breakdown: PriceBreakdown | null;
}

/**
 * Resolves the offer and its pricing for the in-site checkout screens.
 *
 * Shared by the bag and the checkout so both quote the same figures from the same fetch, and so the
 * fallback condition (`resolved` with no `breakdown`) is defined in one place rather than judged twice.
 *
 * @param gwaShopUrl the chip's GWA shop URL, or null for a chip with no rescue offer
 * @param ready false while the caller is still loading the chip; resolution waits for it
 */
export function useCheckoutOffer(gwaShopUrl: string | null, ready: boolean): CheckoutOfferState {
  const [state, setState] = useState<CheckoutOfferState>({ resolved: false, offer: null, breakdown: null });

  useEffect(() => {
    if (!ready) {
      return;
    }

    let cancelled = false;
    void (async () => {
      const offer = await resolveCheckoutOffer(gwaShopUrl);
      // Adoption-worded money only where the rescue is actually named. `rescueName` is nullable even
      // when the programme resolves, and a fallback like "Your rescue credit" on a price line is worse
      // than the default -- so no name means the default label, not a degraded one.
      const breakdown = offer
        ? await fetchPriceBreakdown(offer.planSku, offer.rescueName ? ADOPTION_CREDIT_LABEL : undefined)
        : null;
      if (!cancelled) {
        setState({ resolved: true, offer, breakdown });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, gwaShopUrl]);

  return state;
}
